import { buildApiUrl } from '../config/api.js';

class SeasonPriceService {
  constructor() {
    this.authenticatedFetch = null;
  }

  // Método para establecer la función de fetch autenticada
  setAuthenticatedFetch(fetchFn) {
    this.authenticatedFetch = fetchFn;
  }

  // Obtener todos los precios estacionales
  async getAllSeasonPrices(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.seasonId) params.append('seasonId', filters.seasonId);
      if (filters.vehicleModelId) params.append('vehicleModelId', filters.vehicleModelId);
      
      const url = buildApiUrl(`/api/admin/season-prices?${params.toString()}`);
      
      const response = this.authenticatedFetch 
        ? await this.authenticatedFetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        : await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Error ${response.status}: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error al obtener precios estacionales:', error);
      throw error;
    }
  }

  // Obtener precios por modelo
  async getSeasonPricesByModel(vehicleModelId) {
    try {
      const url = buildApiUrl(`/api/admin/season-prices/model/${vehicleModelId}`);
      
      const response = this.authenticatedFetch 
        ? await this.authenticatedFetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        : await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Error ${response.status}: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error al obtener precios del modelo:', error);
      throw error;
    }
  }

  // Obtener precios por temporada
  async getSeasonPricesBySeason(seasonId) {
    try {
      const url = buildApiUrl(`/api/admin/season-prices/season/${seasonId}`);
      
      const response = this.authenticatedFetch 
        ? await this.authenticatedFetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        : await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Error ${response.status}: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error al obtener precios de la temporada:', error);
      throw error;
    }
  }

  // Obtener precio para una fecha específica
  async getPriceForDate(vehicleModelId, date) {
    try {
      const url = buildApiUrl(`/api/admin/season-prices/price-for-date/${vehicleModelId}?date=${date}`);
      
      const response = this.authenticatedFetch 
        ? await this.authenticatedFetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        : await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Error ${response.status}: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error al obtener precio para fecha:', error);
      throw error;
    }
  }

  // Crear precio estacional
  async createSeasonPrice(priceData) {
    try {
      const url = buildApiUrl('/api/admin/season-prices');
      
      const response = this.authenticatedFetch 
        ? await this.authenticatedFetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(priceData)
          })
        : await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            },
            body: JSON.stringify(priceData)
          });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Error ${response.status}: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error al crear precio estacional:', error);
      throw error;
    }
  }

  // Actualizar precio estacional
  async updateSeasonPrice(id, priceData) {
    try {
      const url = buildApiUrl(`/api/admin/season-prices/${id}`);
      
      const response = this.authenticatedFetch 
        ? await this.authenticatedFetch(url, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(priceData)
          })
        : await fetch(url, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            },
            body: JSON.stringify(priceData)
          });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Error ${response.status}: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error al actualizar precio estacional:', error);
      throw error;
    }
  }

  // Eliminar precio estacional
  async deleteSeasonPrice(id) {
    try {
      const url = buildApiUrl(`/api/admin/season-prices/${id}`);
      
      const response = this.authenticatedFetch 
        ? await this.authenticatedFetch(url, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json'
            }
          })
        : await fetch(url, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Error ${response.status}: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error al eliminar precio estacional:', error);
      throw error;
    }
  }
}

export default new SeasonPriceService();